import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Button from "@rio-cloud/rio-uikit/Button";
import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import { AutoSuggestSuggestion } from "@rio-cloud/rio-uikit/components/autosuggest/AutoSuggest";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import ExpanderPanel from "@rio-cloud/rio-uikit/ExpanderPanel";
import Notification from "@rio-cloud/rio-uikit/Notification";
import Select, { SelectOption } from "@rio-cloud/rio-uikit/Select";
import {
  RootState,
  setTripUpdateDetailDataUpdateDestination,
  setTripUpdateDetailDataUpdateOrigin,
  useAppSelector,
} from "@store";
import { CreateAreaReworkPayload, IRoute, IVehicleInfo, NavigateState, Route, Tolls } from "@types";
import { debounce } from "lodash";
import moment from "moment";
import {
  fetchAutosuggest,
  fetchAutosuggestByCoords,
  fetchRouteOptimze,
  getStopsCheckpoint,
  mountRoute,
} from "src/services/routes";
import {
  defineAreas,
  defineAreasRework,
  defineAreasSelectedIds,
  defineAvoidAreas,
  defineAvoidToll,
  defineDestination,
  defineDistance,
  defineDurationWithStops,
  defineOrigin,
  defineRouteCost,
  defineRouteDeviation,
  defineRoutes,
  defineStops,
  defineVehicleInfo,
  defineWaypoint,
} from "src/store/routes/RoutesSlice";
import Autosuggest from "../Autosuggest";
import CustomSlider from "../CustomSlider/CustomSlider";
import "./routes.css";

import {
  getByAccountAndCostTypeAccountDefaultCost,
  getCalculateFuelCost,
  getCostTypeByAccount,
  getCurrencyTypes,
  getTruckTypes,
} from "../../services/costSimulator";

import {
  CalcFuelStops,
  CalcTools,
  convertMetersToKm,
  formatCategory,
  formatMaskTime,
  formatMetersMaskInNumber,
  formatMoneyInNumber,
  formatPolygon,
  getDnGroupIdFromUrl,
} from "@utils";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getAllCheckpoints } from "src/services/checkpoint";
import { getAllAreas, getAllGlobalAreas } from "../../services/areas";
import { CreateRouteCostPayload, getCostTypeByAccountResponse } from "../../types/costSimulator";
import { Dynamics } from "../Dynamics";
import { calculateOffsetCoordinates } from "../Dynamics/DynamicContentArea";
import { MilkRun, Progress } from "../index";
import { Timelines } from "../Timelines";

import { useMapDrawingContext } from "src/providers/MapDrawingContext";
import {
  setCapacityAutonomy,
  setConsumeAutonomy,
  setEnableAutonomy,
  setExpanded,
  setFuelAutonomy,
  setFuelLevelAutonomy,
  useVia as setViaAction,
  setVisionAutonomy,
} from "src/store/autonomy/autonomySlice";
import SelectCheckpoint from "../Checkpoint";
import { IDinamicSmartRoutePreferences } from "../Dynamics/DinamicSmartRoutePreferences";
import { InputTypeWeight, OptionsWeightType } from "../MilkRun";
import { ProgressOptions } from "../Progress/Trip";
import RowRoute from "./components/Row";
interface DynamicContentDesvioProps {
  isChecked: boolean;
  rangeValue: number;
  onCheckedChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onDeviationChange: (value: number) => void;
}

const DynamicContentDesvio = ({
  isChecked,
  onCheckedChange,
  rangeValue,
  onDeviationChange,
}: DynamicContentDesvioProps) => {
  const formatValueWithUnit = (value: number): string => `${value}m`;

  const parseValueFromUnit = (value: string): number => {
    const numericValue = parseFloat(value.replace(" m", ""));
    return isNaN(numericValue) ? 0 : numericValue;
  };

  const [localInputValue, setLocalInputValue] = useState<string>(formatValueWithUnit(rangeValue));

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value?.replace(/[^0-9.]/g, "");

    let numericValue = parseValueFromUnit(rawValue);

    if (numericValue <= 2000) {
      setLocalInputValue(rawValue);
    }

    if (numericValue >= 0.1 && numericValue <= 2000) {
      onDeviationChange(Number(numericValue.toFixed(1)));
    }
  };

  const handleInputBlur = () => {
    let numericValue = parseValueFromUnit(localInputValue);

    if (numericValue > 2000) {
      numericValue = 2000;
    }

    setLocalInputValue(formatValueWithUnit(numericValue));
  };

  const handleSliderChange = (value: number) => {
    onDeviationChange(Number(value.toFixed(2)));
    setLocalInputValue(formatValueWithUnit(value));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Delete", ","];

    if (allowedKeys.includes(e.key)) return;

    const numericValue = parseValueFromUnit(localInputValue + e.key);

    if (isNaN(numericValue) || numericValue > 2000) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (!isChecked) {
      setLocalInputValue(formatValueWithUnit(0));
      onDeviationChange(0);
    } else {
      if (localInputValue === "1m") {
        setLocalInputValue("1m");
      }
    }
  }, [isChecked]);

  return (
    <div className="display-flex gap-20" style={{ flexDirection: "column" }}>
      <div className="itens-container display-flex gap-20">
        <div className="slider-container">
          <CustomSlider value={rangeValue} minValue={0} maxValue={2000} step={1} onChange={handleSliderChange} />
        </div>

        <div className="input-container">
          <input
            type="text"
            className="form-control"
            value={localInputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>
    </div>
  );
};

type TProps = {
  onChangeRoutes: (routes: Route[], via: any[]) => void;
  onSave?: (
    origin: IRoute,
    destination: IRoute,
    routes: Route[],
    stops: IStop[],
    routeName: string,
    routeCost: CreateRouteCostPayload,
    accountId: string,
    routeRangeToleranceLimit: number | null,
    avoidToll: boolean,
    areas: CreateAreaReworkPayload[],
    vehicle: IVehicleInfo,
    areasSelectedIds: string[],
    distance?: string,
    smartRoutePreference?: IDinamicSmartRoutePreferences,
    selectedFuelOption?: string | number
  ) => void;
  saveLabel?: string;
  trip?: boolean;
  handleCustom?: (s: boolean) => void;
  isCustom?: boolean;
  handleEditDriverTrip?: () => void;
  handleEditVehicleTrip?: () => void;
  isEditTrip?: boolean;
  skipChangeRoute?: React.MutableRefObject<boolean>;
};

export interface IStop {
  id?: string;
  position: any;
  stayTime: string;
  category: string;
  order: number;
  checkpointId?: string | null;
  weight?: number;
  note?: string;
  name?: string;
}

const INITIAL_STOP = {
  position: null,
  stayTime: "00:00",
  category: "REFUELING",
  order: 1,
};

export const convertTimeStringToSeconds = (timeStr: string): number => {
  if (!timeStr || timeStr.trim() === "" || timeStr === "0" || timeStr === "0:00" || timeStr === "0h0min") {
    return 0;
  }

  if (timeStr.includes(":")) {
    const parts = timeStr.split(":");
    if (parts.length !== 2) {
      throw new Error("Formato inválido. Use 'HH:MM' ou 'HhMMmin' (ex.: 01:00 ou 1h00min)");
    }
    const [hoursStr, minutesStr] = parts;
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    if (isNaN(hours) || isNaN(minutes)) {
      throw new Error("Formato inválido. Use 'HH:MM' ou 'HhMMmin'");
    }
    return hours * 3600 + minutes * 60;
  } else if (timeStr.includes("h")) {
    const match = timeStr.match(/(\d+)h(\d+)min/);
    if (!match) {
      throw new Error("Formato inválido. Use 'HH:MM' ou 'HhMMmin'");
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (isNaN(hours) || isNaN(minutes)) {
      throw new Error("Formato inválido. Use 'HH:MM' ou 'HhMMmin'");
    }
    return hours * 3600 + minutes * 60;
  } else {
    throw new Error("Formato inválido. Use 'HH:MM' ou 'HhMMmin'");
  }
};

export const convertSecondsToHours = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return `${hours}h${minutes}min`;
};

const RegisterRoute = ({
  onChangeRoutes,
  onSave,
  saveLabel = "Salvar rota",
  isCustom,
  handleCustom,
  isEditTrip,
  handleEditDriverTrip,
  handleEditVehicleTrip,
  trip,
  skipChangeRoute,
}: TProps) => {
  // Ref para controlar se o waypoint já foi processado
  const processedWaypointRef = useRef<string | null>(null);

  const {
    origin: originStore,
    destination: destinationStore,
    stops,
    routes: routesStore,
    routeCost: routeCostStore,
    routeName,
    accountId,
    routeRangeToleranceLimit,
    avoidToll: routeAvoidToll,
    areasRework: areasStore,
    areasToAvoid: areasToAvoidStore,
    vehicle,
    areasSelectedIds,
  } = useAppSelector((state) => state.routes);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const waypoint = useAppSelector((state) => state.routes.waypoint);
  const [checkpointOptions, setCheckpointOptions] = useState<SelectOption[]>([]);

  const hasDnGroupId = !!getDnGroupIdFromUrl();
  const isMechanicalEmergencyStop = (stop: IStop): boolean =>
    stop.category === "MECHANICAL_EMERGENCY" && !!stop.checkpointId;
  const location = useLocation();
  const hashFromLocation = location.hash;
  const hashFromWindow = typeof window !== "undefined" ? window.location.hash : "";
  const fullHash = hashFromLocation || hashFromWindow;
  const isTripScreen = fullHash.includes("/routing/trips/");
  const { setEnableContextMenuClick } = useMapDrawingContext();

  useEffect(() => {
    if (originStore && destinationStore) {
      setEnableContextMenuClick(true);
    }
    return () => {
      setEnableContextMenuClick(false);
    };
  }, [originStore, destinationStore, setEnableContextMenuClick]);

  useEffect(() => {
    async function fetchCheckpoints() {
      try {
        const data = await getAllCheckpoints();
        const formatted = data.map((checkpoint: any) => ({
          id: checkpoint.id,
          label: checkpoint.name,
        }));
        setCheckpointOptions(formatted);
      } catch (error) {
        console.error("Erro ao carregar checkpoints", error);
      }
    }

    fetchCheckpoints();
  }, []);

  useEffect(() => {
    setAvoidToll(routeAvoidToll);
  }, [routeAvoidToll]);

  useEffect(() => {
    if (!routeId) {
      setVia([]);
    }
  }, []);

  useEffect(() => {
    if (waypoint && waypoint.lat && waypoint.lng) {
      // Verificar se este waypoint já foi processado
      if (processedWaypointRef.current === waypoint.id) {
        return;
      }
      // Verificar se o waypoint já foi processado na via
      const waypointAlreadyProcessed = via.some(
        (stop) => stop.id === waypoint.id || (stop.position.lat === waypoint.lat && stop.position.lng === waypoint.lng)
      );

      if (waypointAlreadyProcessed) {
        processedWaypointRef.current = waypoint.id;
        dispatch(defineWaypoint(null));
        return;
      }

      // Marcar este waypoint como processado
      processedWaypointRef.current = waypoint.id;

      const clearWayPoint = () => {
        // Limpar o waypoint state APÓS adicionar com sucesso
        setTimeout(() => {
          processedWaypointRef.current = null; // Resetar a ref
          dispatch(defineWaypoint(null));
        }, 100);
      };

      const addWaypoint = async () => {
        try {
          const locationName = await fetchAutosuggestByCoords(waypoint.lat, waypoint.lng);

          const newStop: IStop = {
            id: waypoint.id,
            position: {
              lat: waypoint.lat,
              lng: waypoint.lng,
              label: locationName ? locationName : `Ponto de passagem - ${waypoint.lat}/${waypoint.lng}`,
            },
            stayTime: "00:00",
            category: "PASSING_POINT",
            order: via.length + 1,
          };

          setVia((prev) => {
            const newVia = [...prev, newStop];
            clearWayPoint()
            return newVia;
          });

          // REMOVIDO: dispatch(defineWaypoint(null)); - Isso estava causando loop infinito
        } catch (error) {
          processedWaypointRef.current = null; // Resetar a ref em caso de erro
        }
      };

      addWaypoint();
    } else if (waypoint === null) {
      // Resetar a ref quando o waypoint for limpo
      processedWaypointRef.current = null;
    }
  }, [waypoint]);

  //TEMP FOI RETIRADO AS PRESSAS REFERENTE A LÓGICA DO SIMULADOR SE BASEAR NO TIPO DO VEÍCULO
  const [type, setType] = useState("TRUCK");
  const [origin, setOrigin] = useState<any>(null);
  const [destination, setDestination] = useState<any>(null);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [via, setVia] = useState<IStop[]>([]);
  const [viaWithPoints, setViaWithPoints] = useState<{ distance: number; duration: number }[]>([]);
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  //const [stop, setStop] = useState({ ...INITIAL_STOP });
  const [stop, setStop] = useState<IStop>(INITIAL_STOP as IStop);
  const [invalidPosition, setInvalidPosition] = useState(false);

  const [hasRouteRangeLimit, setHasRouteRangeLimit] = useState(Boolean(routeRangeToleranceLimit));
  const [avoidToll, setAvoidToll] = useState(Boolean(routeAvoidToll));
  const [routeRangeLimit, setRouteRangeLimit] = useState(routeRangeToleranceLimit ?? 0);

  const [selectedFuelOption, setSelectedFuelOption] = useState<string | number>(7);
  const [selectedVehicleOption, setSelectedVehicleOption] = useState<string | number>(1);
  const [kmInputValue, setKmInputValue] = useState<number>(0);
  const [priceInputValue, setPriceInputValue] = useState<string>("0,00");

  const fuelType = useRef<{ value: number; label: string }[]>([]);
  const [vehicleType, setVehicleType] = useState<{ value: number; label: string }[]>([]);
  const [tollValue, setTollValue] = useState<number>(0);
  const [fuelValue, setFuelValue] = useState<number>(0);
  const [totalValue, setTotalValue] = useState<number>(0);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [currencyTypeId, setCurrencyTypeId] = useState<number>(0);

  const [operational, setOperational] = useState<getCostTypeByAccountResponse | undefined>();
  const [priceOperational, setPriceOperational] = useState<string>("0,00");

  const [vehicleLenght, setVehicleLenght] = useState<string>(vehicle?.lenght || "");
  const [vehicleHeight, setVehicleHeight] = useState<string>(vehicle?.height || "");
  const [vehicleWidth, setVehicleWidht] = useState<string>(vehicle?.width || "");
  const [vehicleNumberOfAxles, setVehicleNumberOfAxles] = useState<string>(vehicle?.numberOfAxles || "");
  const [vehicleTrailerAxles, setVehicleTrailerAxles] = useState<string>(vehicle.trailerAxles || "");
  const [vehicleMaxAxleLoad, setVehicleMaxAxleLoad] = useState<string>(vehicle.maxAxleLoad || "");
  const [vehicleGrossWeight, setGrossWeight] = useState<string>(vehicle.grossWeight || "");
  const [shippedVHazardousGoods, setVShippedHazardousGoods] = useState<string | number>("");
  const [vehiclePollutantClass, setVehiclePollutantClass] = useState<string | number>("");

  const [searchParams] = useSearchParams();
  const routeId = searchParams.get("id");
  const isUptimeEmergency = !!searchParams?.get('dn-group-id');

  const { durationWithStops, distance: distanceWithStops } = useSelector((state: RootState) => state.routes);

  const [shouldSave, setShouldSave] = useState(false);
  const [areaPages, setAreaPages] = useState(0);
  const [areaData, setAreaData] = useState<CreateAreaReworkPayload[]>([]);
  const [globalAreasFetched, setGlobalAreasFetched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [weightStorage, setWeightStorage] = useState(vehicle?.maxLoadWeight);
  const [selectedStopCheckpoint, setSelectedStopCheckpoint] = useState<IStop>();
  const [weightType, setWeighType] = useState<InputTypeWeight[]>([{ id: "1", label: "Kg" }]);

  const navigateState: any = location.state as NavigateState | null;
  const autonomyState = useAppSelector((state) => state.autonomy);
  const {
    enableAutonomy,
    expanded,
    fuelAutonomy: FuelAutonomy,
    visionAutonomy,
    fuelLevelAutonomy,
    capacityAutonomy,
    consumeAutonomy,
  } = autonomyState;

  useEffect(() => {
    if (navigateState?.smartRoutePreference) {
      setSmartRoutePreference(navigateState.smartRoutePreference);
    }
  }, [navigateState?.smartRoutePreference]);

  useEffect(() => {
    dispatch(setViaAction(via));
  }, [via, dispatch]);

  const [smartRoutePreference, setSmartRoutePreference] = useState<IDinamicSmartRoutePreferences>({
    energy: true,
    fastest: true,
    economic: true,
  });

  const handleSmartRoutePreferenceChange = (preferences: { energy: boolean; fastest: boolean; economic: boolean }) => {
    setSmartRoutePreference(preferences);
  };

  useEffect(() => {
    fetchAreaPagination();
  }, [routeId]);

  useEffect(() => {
    if (vehicle) {
      const {
        lenght,
        height,
        width,
        trailerAxles,
        maxAxleLoad,
        grossWeight,
        numberOfAxles,
        shippedHazardousGoods,
        pollutantClass,
        maxLoadWeight,
        weightType,
      } = vehicle;
      setType(routeId ? vehicle.type : "TRUCK");
      setVehicleLenght(lenght);
      setVehicleHeight(height);
      setVehicleWidht(width);
      setVehicleTrailerAxles(trailerAxles);
      setVehicleMaxAxleLoad(maxAxleLoad);
      setGrossWeight(grossWeight);
      setVehicleNumberOfAxles(numberOfAxles);
      setWeightStorage(maxLoadWeight);
      const filteredWeight = OptionsWeightType.filter((res) => vehicle.weightType === res.label);
      setWeighType(filteredWeight);
      if (shippedHazardousGoods) {
        setVShippedHazardousGoods(shippedHazardousGoods);
      }
      setVehiclePollutantClass(pollutantClass);
    }
  }, [vehicle]);

  useEffect(() => {
    const vw = vehicleGrossWeight ? parseFloat(vehicleGrossWeight) : 0;
    const vt = vehicleTrailerAxles ? parseInt(vehicleTrailerAxles) : 0;
    const vn = vehicleNumberOfAxles ? parseInt(vehicleNumberOfAxles) : 0;
    const sum = vt + vn;
    const cal = vw / sum;

    if (
      vehicleGrossWeight &&
      vehicleTrailerAxles &&
      vehicleNumberOfAxles &&
      vehicleGrossWeight !== "0,00" &&
      vehicleGrossWeight !== "0" &&
      vehicleTrailerAxles !== "0"
    ) {
      setVehicleMaxAxleLoad(cal.toString());
    } else if (vehicleGrossWeight && vehicleTrailerAxles && vehicleNumberOfAxles && vt === 0 && vn === 0 && vw > 0) {
      setVehicleMaxAxleLoad("0,00");
    } else {
      setVehicleMaxAxleLoad("0,00");
    }
  }, [vehicleGrossWeight, vehicleTrailerAxles, vehicleNumberOfAxles]);

  function handleHasRouteRangeLimitChange(event: ChangeEvent<HTMLInputElement>) {
    setHasRouteRangeLimit(event.target.checked);
  }

  function handleRouteRangeLimitChange(value: number) {
    setRouteRangeLimit(value);
  }

  useEffect(() => {
    const fetchCurrencyTypes = async () => {
      try {
        const response = await getCurrencyTypes();
        const realCurrency = response.find((currency) => currency.name.toLowerCase().includes("real"));
        if (realCurrency) {
          setCurrencyTypeId(realCurrency.id);
        }
      } catch (error) {
        console.error("Error fetching currency types:", error);
      }
    };

    fetchCurrencyTypes();
  }, []);

  useEffect(() => {
    if (routeRangeToleranceLimit && routeRangeToleranceLimit > 0) {
      setHasRouteRangeLimit(true);
      setRouteRangeLimit(routeRangeToleranceLimit);
    } else {
      setHasRouteRangeLimit(false);
      setRouteRangeLimit(0);
    }
  }, [routeRangeToleranceLimit]);

  useEffect(() => {
    setOrigin(originStore || null);
    setDestination(destinationStore || null);
  }, [originStore, destinationStore]);

  useEffect(() => {
    setVia(stops);
    setSelectedStopCheckpoint(undefined);
  }, [stops]);

  useEffect(() => {
    // Só aplica os valores do routeCostStore quando estiver editando uma rota existente
    if (routeCostStore && routeId) {
      const costTypeKey = routeCostStore?.costTypes?.[0];
      setSelectedFuelOption(costTypeKey || 7);
      setSelectedVehicleOption(routeCostStore.truckType || 1);
      const tollCost = routeCostStore.tollCost || 0;
      const totalCost = routeCostStore.totalCost || 0;
      setTollValue(tollCost);
      setFuelValue(totalCost - tollCost);

      setTotalValue(totalCost);

      const priceOp = routeCostStore?.valuePerUnit?.[operational?.id as keyof number]?.[0];
      if (priceOp) {
        setPriceOperational(Number(priceOp).toFixed(2).replace(".", ","));
      }

      const valuePerUnit = routeCostStore?.valuePerUnit?.[costTypeKey];

      const kmValue = valuePerUnit ? valuePerUnit?.[0] : 0;
      const priceValue = valuePerUnit ? String(valuePerUnit[1]) : "0";
      setPriceInputValue(priceValue);
      setKmInputValue(kmValue);

      if (priceValue) {
        const numericValue = priceValue.replace(",", ".");
        if (!isNaN(Number(numericValue))) {
          const formattedPriceValue = `${Number(numericValue).toFixed(2).replace(".", ",")}`;
          setPriceInputValue(formattedPriceValue);
        }
      }

      if (totalCost > 0) {
        setShowResults(true);
      }
    }
  }, [routeCostStore, routeId]);

  const skipVia = useRef(false);

  useEffect(() => {
    if (skipVia.current) {
      skipVia.current = false;
      return;
    }

    if (origin && destination && !isSaving) {
      handleRoute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    origin?.id,
    destination?.id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    via.map((v) => v.position?.id || v.position?.lat + "," + v.position?.lng).join(","),
    avoidToll,
    type,
    vehicleWidth,
    vehicleHeight,
    vehicleLenght,
    vehicleGrossWeight,
    vehicleMaxAxleLoad,
    vehicleNumberOfAxles,
    vehicleTrailerAxles,
    shippedVHazardousGoods,
    areaData,
    areasToAvoidStore,
    skipChangeRoute?.current,
  ]);

  const saveChanges = () => {
    if (!origin) {
      setOriginError(true);
      Notification.error("Campo Origem é obrigatório");
    }
    if (!destination) {
      setDestinationError(true);
      Notification.error("Campo Destino é obrigatório");
    }

    if (!origin || !destination) return;

    const saveOrigin = {
      lat: origin.lat,
      lng: origin.lng,
      label: origin.label,
      originChecklistId: origin?.originChecklistId,
    };

    const saveDestination = {
      lat: destination.lat,
      lng: destination.lng,
      label: destination.label,
      destinyChecklistId: destination?.destinyChecklistId,
    };

    const operationID = parseInt(operational?.id as string) || 921;

    const saveRouteCost: CreateRouteCostPayload = {
      route: "",
      costTypes: [parseInt(selectedFuelOption as string), operationID],
      valuePerUnit: {
        [selectedFuelOption.toString()]: [
          kmInputValue,
          parseFloat(priceInputValue.replace(/[^0-9.,]/g, "").replace(",", ".")),
        ],
        [operationID]: [formatMoneyInNumber(priceOperational) || 0],
      },
      totalCost: totalValue,
      tollCost: tollValue,
      fuelCost: fuelValue,
      currencyType: currencyTypeId,
      truckType: parseInt(selectedVehicleOption as string),
    };

    const vehicleInfo: IVehicleInfo = {
      grossWeight: vehicleGrossWeight,
      height: vehicleHeight,
      width: vehicleWidth,
      lenght: vehicleLenght,
      type: type,
      maxAxleLoad: vehicleMaxAxleLoad,
      numberOfAxles: vehicleNumberOfAxles,
      trailerAxles: vehicleTrailerAxles,
      shippedHazardousGoods: type === "TRUCK" ? shippedVHazardousGoods?.toString() : "NONE",
      pollutantClass: vehiclePollutantClass?.toString(),
      maxLoadWeight: weightStorage,
      weightType: weightType?.[0]?.label ?? "",
    };

    setIsSaving(true);
    dispatch(defineOrigin(saveOrigin));
    dispatch(defineDestination(saveDestination));
    dispatch(defineStops(via));
    dispatch(defineRouteDeviation(routeRangeLimit ?? 0));
    dispatch(defineRouteCost(saveRouteCost));
    dispatch(defineAvoidToll(avoidToll));
    dispatch(defineVehicleInfo(vehicleInfo));
    setShouldSave(true);
  };

  useEffect(() => {
    if (shouldSave) {
      if (onSave && originStore && destinationStore) {
        onSave(
          originStore,
          destinationStore,
          routesStore,
          stops,
          routeName,
          routeCostStore,
          accountId,
          routeRangeToleranceLimit ?? 0,
          routeAvoidToll,
          areasStore,
          vehicle,
          areasSelectedIds,
          distanceWithStops,
          smartRoutePreference,
          selectedFuelOption
        );
      }
    }
  }, [shouldSave, originStore, destinationStore, routeCostStore]);

  useEffect(() => {
    onChangeRoutes(routes, via);
    dispatch(defineRoutes(routes));
  }, [routes]);

  const fetchGlobalAreas = async () => {
    try {
      if (!globalAreasFetched) {
        const res = await getAllGlobalAreas();

        if (res && res.length > 0) {
          const globalAreasWithSelected = res.map(
            (area) =>
            ({
              ...area,
              id: Number(area.id),
              selected: true,
              areaTypeId: area.geometryType === "POLYGON" ? 1 : 2,
            } as CreateAreaReworkPayload)
          );

          const avoidAreas = globalAreasWithSelected.filter((a) => a.interestType === "AVOID");
          const interestAreas = globalAreasWithSelected.filter((a) => a.interestType === "INTEREST");

          dispatch(defineAreasRework([...avoidAreas, ...interestAreas]));
          dispatch(defineAreas([...avoidAreas, ...interestAreas]));

          if (avoidAreas.length > 0) {
            const formatted = avoidAreas
              .map((area) => {
                const isCircle = area.circleRadius !== null && area.circleRadius !== undefined;
                if (isCircle) {
                  const center = area.points[0];
                  const radius = area.circleRadius!;
                  const offset = calculateOffsetCoordinates(center.lat, center.lng, radius);
                  return `corridor:${center.lat},${center.lng};${offset.lat},${offset.lng};r=${Math.round(radius)}`;
                }
                return formatPolygon(area.points);
              })
              .join("|");

            dispatch(defineAvoidAreas({ formatted }));
          }

          const selectedIds = globalAreasWithSelected.map((area) => String(area.id));
          dispatch(defineAreasSelectedIds(selectedIds));
        }

        setGlobalAreasFetched(true);
      }
    } catch (error) {
      Notification.error("Não foi possivel buscar as áreas globais");
    }
  };

  const fetchAreaPagination = async () => {
    try {
      const res = await getAllAreas("", 1, 100000, "name,asc", routeId ?? undefined);
      setAreaPages(res.pages);
      setAreaData(res.items);
    } catch (error) {
      console.error("Não foi possível buscar a paginação das áreas");
    }
  };
  const backup = useAppSelector((state) => state.trips.backup);

  const prepareOrderVia = () => {
    if (!enableAutonomy) {
      return via.map((t) => ({ lat: t.position.lat, lng: t.position.lng }));
    }
    return via
      .map((t) => ({ lat: t.position.lat, lng: t.position.lng }))
      .filter((item) => item.lat && item.lng && !isNaN(item.lat) && !isNaN(item.lng));
  };

  const createVehicleData = () => ({
    height: formatMetersMaskInNumber(vehicleHeight) || 0,
    width: formatMetersMaskInNumber(vehicleWidth) || 0,
    length: formatMetersMaskInNumber(vehicleLenght) || 0,
    numberOfAxles: parseInt(vehicleNumberOfAxles) || 0,
    trailerAxles: parseInt(vehicleTrailerAxles) || 0,
    grossWeight: formatMetersMaskInNumber(vehicleGrossWeight, "T") || 0,
    maxWeightAxle: parseFloat(vehicleMaxAxleLoad?.slice(0, 4)) * 1000 || 0,
    vehicleType: type,
    shippedHazardousGoods:
      type === "TRUCK" ? (shippedVHazardousGoods !== "" ? shippedVHazardousGoods : "NONE") : "NONE",
  });

  const createBackupVehicleData = () => ({
    height: backup?.vehicleVocationalInfo?.height,
    width: backup?.vehicleVocationalInfo?.width,
    length: backup?.vehicleVocationalInfo?.comTotal,
    numberOfAxles: backup?.vehicleVocationalInfo?.numberAxle,
    trailerAxles: backup?.vehicleVocationalInfo?.trailerAxle,
    grossWeight: backup?.vehicleVocationalInfo?.maxWeight,
    maxWeightAxle: backup?.vehicleVocationalInfo?.maxWeightAxle,
    vehicleType: backup?.vehicleVocationalInfo?.vehicleType,
    shippedHazardousGoods:
      type === "TRUCK" ? (shippedVHazardousGoods !== "" ? shippedVHazardousGoods : "NONE") : "NONE",
  });

  const createRouteBody = (orderVia: any) => ({
    origin: { lat: origin.lat, lng: origin.lng },
    destination: { lat: destination.lat, lng: destination.lng },
    via: orderVia,
    vehicle: createVehicleData(),
    departureTime: moment().format("YYYY-MM-DDTHH:mm:ss"),
    avoidToll,
    transport: {
      mode: type && type !== "" ? type : "TRUCK",
    },
    avoidAreas: areasToAvoidStore.formatted,
  });

  const createBackupRouteBody = () => {
    const stopFiltered = backup?.stops
      ?.map((stop: { stop: IStop }) => ({ lat: stop?.stop?.position?.lat, lng: stop?.stop?.position?.long }))
      ?.filter((res: { lat: number; lng: number }) => res?.lat && res?.lng);

    return {
      origin: { lat: backup?.originRoute.lat, lng: backup?.originRoute.long },
      destination: { lat: backup?.destinyRoute.lat, lng: backup?.destinyRoute.long },
      via: stopFiltered,
      vehicle: createBackupVehicleData(),
      departureTime: moment().format("YYYY-MM-DDTHH:mm:ss"),
      avoidToll: backup?.roadParameters?.avoidToll,
      transport: {
        mode: backup?.vehicleVocationalInfo?.vehicleType,
      },
      avoidAreas: areasToAvoidStore.formatted,
    };
  };

  const extractTolls = (sections: any[]) => {
    const tools: Tolls[] = [];
    sections?.forEach((section) => {
      section.tolls?.forEach((toll: Tolls) => {
        tools.push(toll);
      });
    });
    return tools;
  };

  const processRouteResponse = (response: any) => {
    const tools = extractTolls(response.routes?.[0]?.sections || []);
    const calc = CalcTools(tools);
    setTollValue(calc);
    setRoutes(response.routes);

    const points = response.routes?.[0]?.sections
      .map((res: any) => ({
        distance: res.summary.length,
        duration: res.summary.duration,
      }))
      .filter((res: any) => res.distance !== 0 || res.duration !== 0);

    const { distance, duration } = (response.routes[0]?.sections || []).reduce(
      (acum: any, curr: any) => {
        acum.distance += curr.summary.length;
        acum.duration += curr.summary.duration;
        return acum;
      },
      { distance: 0, duration: 0 }
    );

    const totalStayTimeSeconds = (via || []).reduce((acc, curr) => {
      return acc + convertTimeStringToSeconds(curr.stayTime);
    }, 0);

    dispatch(defineDistance(convertMetersToKm(distance)));
    const totalDurationSeconds = duration + totalStayTimeSeconds;
    dispatch(defineDurationWithStops(convertSecondsToHours(totalDurationSeconds)));

    setViaWithPoints(points);
  };

  const handleRoute = async () => {
    if (!origin || !destination) return;

    const orderVia = prepareOrderVia();

    const body = createRouteBody(orderVia);
    const lastedBody = createBackupRouteBody();

    const isEqualBody = JSON.stringify(body) === JSON.stringify(lastedBody);
    if (skipChangeRoute?.current && isEqualBody && isEditTrip) {
      return;
    }

    const response = await mountRoute(body);
    processRouteResponse(response);

    if (enableAutonomy) {
      onSuggestionVia();
    }
  };

  const [openModal, setOpenModal] = useState(false);

  const DynamicContentPreferencias = () => {
    return (
      <fieldset className="col-sm-4">
        <p>
          <Checkbox checked={avoidToll} onChange={() => setAvoidToll((previous) => !previous)} label="Evitar pedágio" />
        </p>
      </fieldset>
    );
  };

  const fetchDataAutosuggestAddress = async (text: string) => {
    const response = await fetchAutosuggest(text);
    const data: AutoSuggestSuggestion[] = [];
    response.forEach((i) => data.push({ label: i.title, description: i.address.label, ...i } as any));
    return data;
  };

  const onClear = (index: number) => {
    setVia((previous) => {
      const newVia = previous
        .filter((_, i) => index !== i)
        .map((item, i) => ({
          ...structuredClone(item),
          order: i + 1,
        }));

      return newVia;
    });
  };

  const reorderArray = (index: number, direction: 1 | -1) => {
    if (index < 0 || index >= via.length) return;

    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= via.length) return;

    const newArray = [...via];
    [newArray[index], newArray[newIndex]] = [newArray[newIndex], newArray[index]];

    setVia(newArray);
  };

  const handleChangeLength = (value: string) => {
    setVehicleLenght(value);
  };

  const handleChangeHeight = (value: string) => {
    setVehicleHeight(value);
  };

  const handleChangeWidth = (value: string) => {
    setVehicleWidht(value);
  };

  const priceInputRef = useRef<HTMLInputElement>(null);
  const profile = useAppSelector((state) => state.login.userProfile);
  const accountIdG = profile?.account;
  const routeDate = useAppSelector((state) => state.routes);

  const hasFetchedForFuel = useRef(false);
  const previousFuelLabel = useRef<string | null>(null);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  const selectedFuelLabel =
    fuelType.current.find((fuel) => fuel.value == selectedFuelOption)?.label.toLowerCase() || "";

  useEffect(() => {
    fetchFuelTypes();
    fetchVehicleTypes();
  }, []);

  useEffect(() => {
    if (selectedFuelOption && !initialFetchDone) {
      fetchAccountDefaultCosts();
      setInitialFetchDone(true);
    }
  }, [selectedFuelOption, initialFetchDone]);

  useEffect(() => {
    if (routeId && selectedFuelOption !== Number(routeDate.routeCost?.costTypes?.at(0))) {
      fetchAccountDefaultCosts();
    }
  }, [selectedFuelOption, routeId, routeDate.routeCost?.costTypes]);

  useEffect(() => {
    if (!routeId && Boolean(selectedFuelOption.toString())) {
      if (previousFuelLabel.current && previousFuelLabel.current !== selectedFuelLabel) {
        hasFetchedForFuel.current = false;
      }

      if (!hasFetchedForFuel.current) {
        fetchAccountDefaultCosts();
        hasFetchedForFuel.current = true;
      }

      previousFuelLabel.current = selectedFuelLabel;
    }
  }, [selectedFuelOption, selectedFuelLabel, routeId]);

  const fetchAccountDefaultCosts = async () => {
    try {
      if (!accountIdG || !selectedFuelOption || selectedFuelOption === 0) {
        return;
      }

      const selectedFuel = fuelType.current.find((fuel) => fuel.value == selectedFuelOption);

      if (selectedFuel?.value) {
        const response = await getByAccountAndCostTypeAccountDefaultCost(accountIdG, Number(selectedFuel?.value));

        if (response?.valuePerUnit !== undefined) {
          const numericValue = parseFloat(response.valuePerUnit.toString().replace(",", "."));
          if (!isNaN(numericValue)) {
            const formattedPriceValue = `${numericValue.toFixed(2).replace(".", ",")}`;

            setPriceInputValue(formattedPriceValue);
          }
        }
      }

      // Busca valor padrão para custo operacional em novas rotas
      if (!routeId && operational?.id) {
        try {
          const operationalResponse = await getByAccountAndCostTypeAccountDefaultCost(
            accountIdG,
            Number(operational.id)
          );
          if (operationalResponse?.valuePerUnit !== undefined) {
            const operationalValue = operationalResponse.valuePerUnit.toString();
            setPriceOperational(Number(operationalValue).toFixed(2).replace(".", ","));
          } else {
            setPriceOperational("0,00");
          }
        } catch (error) {
          setPriceOperational("0,00");
        }
      }
    } catch (error) {
      console.error("Error fetching account default cost:", error);
    }
  };

  const fetchFuelTypes = async () => {
    try {
      const response = await getCostTypeByAccount(accountIdG);
      const operationalFind = response.find((res) => res.name === "Custo Operacional");

      const formattedFuelTypes = response
        .filter((res) => res.name !== "Custo Operacional")
        .map((item: any) => ({
          value: item.id,
          label: item.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      fuelType.current = formattedFuelTypes;
      setOperational(operationalFind);

      const currentFuelOption = formattedFuelTypes.find((fuel) => fuel.value === selectedFuelOption);

      if (currentFuelOption) {
        setSelectedFuelOption(selectedFuelOption);
      } else {
        setSelectedFuelOption(formattedFuelTypes[0]?.value || 7);
      }
    } catch (error) {
      console.error("Error fetching cost types by account id:", error);
      setSelectedFuelOption(7);
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const response = await getTruckTypes();
      const formattedVehicleTypes = response.map((item: any) => ({
        value: item.id,
        label: item.name,
        ...item,
      }));
      setVehicleType(formattedVehicleTypes);
    } catch (error) {
      console.error("Error fetching vehicle types:", error);
    }
  };

  useEffect(() => {
    const fetchFuel = async () => {

      if (origin && destination) {
        try {
          await fetchCalculateFuelCost();
        } catch (error) {
          console.error("Error fetching fuel cost:", error);
        }
      }
    };
    const debouncedFetchFuel = debounce(fetchFuel, 500);

    debouncedFetchFuel();

    return () => {
      debouncedFetchFuel.cancel();
    };
  }, [selectedFuelOption, kmInputValue, priceInputValue, distanceWithStops, origin?.id, destination?.id]);

  const fetchCalculateFuelCost = async (): Promise<void> => {
    try {

      const isKmEmpty = !kmInputValue || kmInputValue === 0;
      const isPriceEmpty = !priceInputValue || priceInputValue === "0,00" || priceInputValue === "0.00" || priceInputValue === "";

      if (!distanceWithStops || distanceWithStops === "0km" || isKmEmpty || isPriceEmpty) {
        setFuelValue(0);
        return;
      }

      const numericFuelOption =
        typeof selectedFuelOption === "string" ? parseInt(selectedFuelOption) : selectedFuelOption;
      const priceMatch = priceInputValue.match(/\d+(\.\d+)?/g);
      const parsedPriceInputValue = priceMatch ? parseFloat(priceMatch.join(".")) : 0;

      if (isNaN(parsedPriceInputValue)) {
        throw new Error("Invalid price input value");
      }

      const distanceString = distanceWithStops.toLowerCase();
      let distanceValue = 0;

      if (distanceString.includes('m') && !distanceString.includes('km')) {
        distanceValue = Math.round(parseFloat(distanceString));
      } else if (distanceString.includes('km')) {
        const distanceInKm = parseFloat(distanceString);

        if (distanceInKm >= 1) {
          distanceValue = Math.round(convertKmToMeters(distanceInKm));
        } else {
          distanceValue = Math.round(distanceInKm * 1000);
        }
      }

      const response = await getCalculateFuelCost(
        numericFuelOption,
        kmInputValue,
        parsedPriceInputValue,
        distanceValue
      );

      const fuelCost = response.data.result.value;
      setFuelValue(!isFinite(fuelCost) ? 0 : fuelCost);
    } catch (error) {
      console.error("Error fetching fuel cost:", error);
      setFuelValue(0);
    }
  };

  useEffect(() => {
    const tv = tollValue || 0;
    const fv = fuelValue || 0;
    const po = formatMoneyInNumber(priceOperational) || 0;
    const total = tv + fv + po;
    setTotalValue(total);
    if (tollValue !== null && fuelValue !== null) {
      setShowResults(true);
    }
  }, [tollValue, fuelValue, priceOperational]);

  const convertKmToMeters = (distanceInKm: number): number => {
    return distanceInKm * 1000;
  };

  const handleVehicleOptionChange = (newValue: string | number) => {
    setSelectedVehicleOption(newValue);
  };

  const handleShippedHazardousGoodsType = (newValue: string | number) => {
    setVShippedHazardousGoods(newValue);
  };

  const handleFuelOptionChange = (newValue: string | number) => {
    setSelectedFuelOption(newValue);
  };

  const checkEcqualField = (
    field?: string | boolean | number | null,
    fieldState?: string | boolean | number | null
  ) => {
    return field === fieldState;
  };

  const checkEcqualStops = (arr1: IStop[], arr2: IStop[]) => {
    const ids1 = arr1.map((i) => i.position.id).sort((a: string, b: string) => a.localeCompare(b));
    const ids2 = arr2.map((i) => i.position.id).sort((a: string, b: string) => a.localeCompare(b));

    return JSON.stringify(ids1) === JSON.stringify(ids2);
  };

  const handleCheckCustom = useCallback(() => {
    const isTrip = searchParams.get("trip");
    if (!!isTrip) {
      const isEcqualObj = {
        Origin: checkEcqualField(origin?.id, routeDate.origin?.id as string), // ok
        Destiny: checkEcqualField(destination?.id, routeDate.destination?.id as string), // ok
        Stops: checkEcqualStops(routeDate.stops, via), // ok
        AvoidToll: checkEcqualField(avoidToll, routeDate.avoidToll), // ok
        Type: checkEcqualField(type, routeDate.vehicle.type), // ok
        VehicleNumerOfAxles: checkEcqualField(vehicleNumberOfAxles, routeDate.vehicle.numberOfAxles), // ok
        NumberOfTraxles: checkEcqualField(vehicleTrailerAxles, routeDate.vehicle.numberOfAxles), // ok
        Lenght: checkEcqualField(vehicleLenght, routeDate.vehicle.lenght), //ok
        Height: checkEcqualField(vehicleHeight, routeDate.vehicle.height), // ok
        Width: checkEcqualField(vehicleWidth, routeDate.vehicle.width), // ok
        GrossWeight: checkEcqualField(vehicleGrossWeight, routeDate.vehicle.grossWeight), // ok
        MaxeLoad: checkEcqualField(vehicleMaxAxleLoad, routeDate.vehicle.maxAxleLoad ?? "0,00"), // ok
        PoluentClass: checkEcqualField(vehiclePollutantClass?.toString(), routeDate.vehicle.pollutantClass), // ok
        RouteRangeTollerance: checkEcqualField(routeRangeToleranceLimit, routeDate.routeRangeToleranceLimit), //ok
        SelectedFuelOption:
          selectedFuelOption === 0
            ? true
            : !!routeDate.routeCost?.costTypes?.find((res) => Number(res) === Number(selectedFuelOption)), // ok
      };

      const isAnyFalse = Object.values(isEcqualObj).some((value) => !value);

      if (isAnyFalse) {
        handleCustom?.(true);
      } else {
        handleCustom?.(false);
      }
    }
  }, [
    searchParams,
    origin?.id,
    routeDate.origin?.id,
    routeDate.destination?.id,
    routeDate.stops,
    routeDate.avoidToll,
    routeDate.vehicle.type,
    routeDate.vehicle.numberOfAxles,
    routeDate.vehicle.lenght,
    routeDate.vehicle.height,
    routeDate.vehicle.width,
    routeDate.vehicle.grossWeight,
    routeDate.vehicle.maxAxleLoad,
    routeDate.vehicle.pollutantClass,
    routeDate.routeRangeToleranceLimit,
    routeDate.routeCost?.costTypes,
    destination?.id,
    via,
    avoidToll,
    type,
    vehicleNumberOfAxles,
    vehicleTrailerAxles,
    vehicleLenght,
    vehicleHeight,
    vehicleWidth,
    vehicleGrossWeight,
    vehicleMaxAxleLoad,
    vehiclePollutantClass,
    routeRangeToleranceLimit,
    selectedFuelOption,
  ]);

  const [originError, setOriginError] = useState(false);
  const [destinationError, setDestinationError] = useState(false);

  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!globalAreasFetched && !routeId) {
      fetchGlobalAreas();
    }
  }, [globalAreasFetched, routeId]);

  const [stopTypes, setStopTypes] = useState<string[]>();
  const [typeModal, setTypeModal] = useState<"ORIGIN" | "DESTINY" | "STOP">("ORIGIN");

  const handleUpdateForType = (checkpoint?: IStop) => {
    switch (typeModal) {
      case "ORIGIN":
        handleOriginCheckpoint(checkpoint);
        break;
      case "DESTINY":
        handleDestinationCheckpoint(checkpoint);
        break;
      case "STOP":
        handleUpdateVia(checkpoint);
        break;
      default:
        handleUpdateVia(checkpoint);
        break;
    }
  };

  const handleOriginCheckpoint = (originCheckpoint?: IStop) => {
    const updatedOrigin = {
      ...origin,
      originChecklistId: originCheckpoint?.checkpointId,
    };
    setOrigin(updatedOrigin);
    if (origin) {
      dispatch(defineOrigin(updatedOrigin));
    }
    setOpen(false);
  };

  const handleDestinationCheckpoint = (destinationCheckpoint?: IStop) => {
    const updatedDestination = {
      ...destination,
      destinyChecklistId: destinationCheckpoint?.checkpointId,
    };
    setDestination(updatedDestination);
    if (destination) {
      dispatch(defineDestination(updatedDestination));
    }
    setOpen(false);
  };

  const handleUpdateVia = (stop?: IStop) => {
    if (!stop) return;

    const index = via.findIndex((item) => item.order === stop.order);

    if (index !== -1) {
      const updatedVia = [...via];
      updatedVia[index] = stop;
      setVia(updatedVia);
      setOpen(false);
    }
  };

  const calculateEffectiveLoading = (arr: any, max: number) => {
    let currentWeight = 0;
    let totalLoaded = 0;

    for (const item of arr) {
      if (item.type === "LOADING") {
        if (currentWeight + item.weight <= max) {
          currentWeight += item.weight;
          totalLoaded += item.weight;
        }
      } else if (item.type === "UNLOADING") {
        currentWeight = Math.max(0, currentWeight - item.weight);
      }
    }

    return {
      totalLoaded,
      finalWeight: currentWeight,
    };
  };

  const skipEffect = useRef(false);

  const handleCalcMilkRun = async () => {
    const stopsWithId = via.map((stop, idx) => ({
      ...stop,
      id: stop.id ?? (idx + 1).toString(),
    }));

    const milkRunStops = stopsWithId.filter(
      (stop) =>
        (stop.category === "LOADING" ||
          stop.category === "UNLOADING") &&
        stop.position &&
        stop.position.lat != null &&
        stop.position.lng != null
    );



    const viaMapped = milkRunStops.map((stop) => {
      return {
        order: stop.order,
        id: stop.id,
        lat: stop.position.lat,
        lng: stop.position.lng,
        type: stop.category,
        weight: stop.weight,
      };
    });

    const body = {
      origin: {
        lat: origin.lat,
        lng: origin.lng,
      },
      destination: {
        lat: destination.lat,
        lng: destination.lng,
      },
      departureTime: moment().format("YYYY-MM-DDTHH:mm:ss"),
      metric: "TIME",
      vehicle: {
        height: formatMetersMaskInNumber(vehicleHeight) || 0,
        width: formatMetersMaskInNumber(vehicleWidth) || 0,
        length: formatMetersMaskInNumber(vehicleLenght) || 0,
        numberOfAxles: parseInt(vehicleNumberOfAxles) || 0,
        trailerAxles: parseInt(vehicleTrailerAxles) || 0,
        grossWeight: formatMetersMaskInNumber(vehicleGrossWeight, "T") || 0,
        maxWeightAxle: parseFloat(vehicleMaxAxleLoad?.slice(0, 4)) * 1000 || 0,
        maxWeight: weightStorage,
        vehicleType: type?.toLowerCase(),
        pollutantClass: vehiclePollutantClass,
        shippedHazardousGoods:
          type === "TRUCK" ? (shippedVHazardousGoods !== "" ? shippedVHazardousGoods : "NONE") : "NONE",
      },
      avoidToll: avoidToll,
      transport: {
        mode: type?.toLocaleLowerCase(),
      },
      via: viaMapped,
    };

    try {
      const response = await fetchRouteOptimze(body);

      const newroutes = routes.map((route, index) => {
        if (index === 0) {
          return {
            ...route,
            sections: route.sections.map((section, sectionIndex) => {
              if (sectionIndex === 0) {
                return {
                  ...section,
                  polyline: response.polyline,
                };
              }
              return section;
            }),
          };
        }
        return route;
      });

      const optimizedStops: IStop[] = [];

      [...response.via]
        .sort(
          (
            a: { id: string; order: number },
            b: { id: string; order: number }
          ) => a.order - b.order
        )
        .forEach((optimized: { id: string; order: number }) => {
          const stop = milkRunStops.find(
            (item) => item.id === optimized.id
          );

          if (stop) {
            optimizedStops.push(stop);
          }
        });

      const newVia: IStop[] = [...stopsWithId];

      let optimizedIndex = 0;

      newVia.forEach((stop, index) => {
        const isMilkRunStop =
          (stop.category === "LOADING" ||
            stop.category === "UNLOADING") &&
          stop.position &&
          stop.position.lat != null &&
          stop.position.lng != null;

        if (!isMilkRunStop) {
          return;
        }

        const optimizedStop = optimizedStops[optimizedIndex];

        if (optimizedStop) {
          newVia[index] = optimizedStop;
        }

        optimizedIndex++;
      });

      const reorderedVia: IStop[] = newVia.map(
        (stop, index) => ({
          ...stop,
          order: index + 1,
        })
      );

      skipEffect.current = true;
      onChangeRoutes(newroutes, reorderedVia);
      setVia(reorderedVia);
      const res = calculateEffectiveLoading(response.via, totalCheckpoint);
      setTotalCheckpoint(res.finalWeight);
    } catch (error: any) {
      const message = error?.response?.data?.message;

      if (message) {
        Notification.error(message);
      } else {
        Notification.error("Não foi possível calcular a rota com MilkRun.");
      }
    }
  };

  const onSuggestionVia = () => {
    const idxs = CalcFuelStops(viaWithPoints, {
      consumption: consumeAutonomy,
      initialFuel: fuelLevelAutonomy,
      tankCapacity: capacityAutonomy,
    });

    const existingRefuelingStops = via.filter((stop) => stop.category === "REFUELING");
    if (existingRefuelingStops.length > 0) {
      return;
    }

    const viaCopy = [...via];

    idxs.forEach(({ index, stops }) => {
      const newStops = Array.from({ length: stops }, (_, i) => ({
        position: { label: null },
        stayTime: "00:00",
        category: "REFUELING",
        order: i + 1,
      }));
      viaCopy.splice(index, 0, ...newStops);
    });

    setVia(viaCopy);
    skipVia.current = true;
  };

  useEffect(() => {
    if (skipEffect.current) {
      skipEffect.current = false; // resetar após ignorar
      return;
    }

    if (via) {
      const totalCheckpointFiltered = via
        ?.filter((res) => res.category === "LOADING")
        .reduce((acc, res) => {
          const valor = res?.weight ?? 0;
          return acc + (isNaN(valor) ? 0 : valor);
        }, 0);

      setTotalCheckpoint(totalCheckpointFiltered);
    }
  }, [via]);

  useEffect(() => {
    const getStops = async () => {
      const response = await getStopsCheckpoint();
      const types: string[] = response.data.result || [];
      if (!types.includes("MECHANICAL_EMERGENCY")) {
        types.push("MECHANICAL_EMERGENCY");
      }
      if (!types.includes("OTHERS")) {
        types.push("OTHERS");
      }
      setStopTypes(types);
    };

    getStops();
  }, []);

  const [totalCheckpoint, setTotalCheckpoint] = useState(0);

  const handleWeightType = (item?: InputTypeWeight) => {
    if (item?.id) setWeighType([item]);
  };

  const [fuelOptionError, setFuelOptionError] = useState(false);
  const fuelSelectRef = useRef<HTMLSelectElement>(null);
  const [search] = useSearchParams();
  const currentParams = new URLSearchParams(search);

  const onContent = (idx?: number) => {
    const index = idx ?? 0;
    const resolvedIndex = isUptimeEmergency && index >= 2 ? index + 1 : index;
    navigate(ProgressOptions[resolvedIndex]?.route + "?" + currentParams.toString(), {
      state: { edit: navigateState?.edit, cvs: navigateState?.csv },
    });
  };

  const [errorPrice, setErrorPrice] = useState(false);
  const [errorKm, setErrorKm] = useState(false);

  const ValidInputSmartValues = () => {
    const isSmartRouteValid = smartRoutePreference.economic || smartRoutePreference.energy;
    const isPriceValid = priceInputValue !== "0,00" && kmInputValue !== 0;
    return isSmartRouteValid ? isPriceValid : true;
  };

  const isValidSmart = ValidInputSmartValues();

  return (
    <>
      <div className="display-flex flex-column gap-10 ">
        {isTripScreen || isEditTrip ? (
          <div>
            <Progress.Trip selected={5} onSelectedChanged={onContent} step={isUptimeEmergency ? 2 : 3} hideVehicle={isUptimeEmergency} />
          </div>
        ) : null}

        <div className="callout callout-primary border-none display-flex align-items-center shadow-smooth">
          <div className="flex-1">
            <div className="text-medium text-size-20 line-height-125rel display-flex align-items-center">
              <span className="rioglyph rioglyph-road text-size-16 margin-right-10"></span>
              <span className="text-size-16 text-medium line-height-15">
                Distância: {distanceWithStops?.toLowerCase()?.replace(/\./g, ",") || "0km"}
              </span>
            </div>
          </div>
          <div className="display-flex justify-content-center" style={{ flex: 1 }}>
            <h1 className="text-color-primary">{durationWithStops}</h1>
          </div>
        </div>

        <div className="margin-top-5 padding-x-5 padding-y-5">
          {/* <Timelines.Route stops={viaWithPoints} /> */}
          <Timelines.RouteV2 stops={viaWithPoints} duration={durationWithStops} />
        </div>

        <div className="gap-10 display-flex flex-column">
          <RowRoute
            col1={
              <div className="align-self-end display-flex flex-column">
                <label className={`text-color-dark`}>Checklist de origem</label>
                <Button
                  className="border-radius-small align-self-start"
                  disabled={!origin}
                  bsStyle={origin?.originChecklistId && origin?.originChecklistId !== '' ? Button.DEFAULT : Button.PRIMARY}
                  onClick={() => {
                    setSelectedStopCheckpoint({
                      category: 'ORIGIN',
                      order: 0,
                      position: origin.position,
                      stayTime: '00:00',
                      checkpointId: origin?.originChecklistId,
                      id: origin.id,
                      name: origin.label,
                      note: '',
                      weight: 0,
                    });
                    setTypeModal('ORIGIN');
                    setOpen(true);
                  }}
                >
                  {origin?.originChecklistId && origin?.originChecklistId !== '' ? "Editar checklist" : "Vincular checklist"}
                </Button>
              </div>
            }
            col2={
              <div className="width-100pct">
                <label className={`text-color-dark ${originError ? "text-color-danger" : ""}`}>Origem</label>
                <div
                  className={`display-flex width-100pct align-items-center ${originError ? "form-group has-error no-margin-bottom" : ""
                    }`}
                  style={{ marginBottom: 0, paddingLeft: 0 }}
                >
                  <Autosuggest
                    fetchData={fetchDataAutosuggestAddress}
                    placeholder="Informe a origem"
                    icon="rioglyph-start"
                    defaultValue={origin?.label || origin?.description || ""}
                    defaultDescription={origin?.description}
                    defaultPosition={{ lat: origin?.lat, lng: origin?.lng }}
                    onClear={() => setOrigin(null)}
                    onSuggestionSelected={async (e: any, i: any) => {
                      const { lat, lng }: any = i?.suggestion?.position;
                      const originTemp = {
                        ...(i.suggestion as any).position,
                        ...i.suggestion,
                        id: i.suggestion.id,
                        originChecklistId: origin?.originChecklistId,
                      };
                      setOriginError(false);
                      setOrigin(originTemp);
                      dispatch(defineOrigin(originTemp));
                      if (isEditTrip) {
                        dispatch(setTripUpdateDetailDataUpdateOrigin({ ...originTemp, long: lng, lat: lat }));
                      }
                    }}
                  />
                </div>
              </div>
            }
            col3={
              <Button
                bsStyle={Button.PRIMARY}
                disabled={!origin || !destination || !via.every((res) => res.position.lat)}
                iconOnly
                iconName="rioglyph-plus-light"
                onClick={() => setOpenModal(true)}
                className="margin-left-10"
              />
            }
          />

          {via.map((item, index) => {
            const hasError = weightStorage > 0 ? totalCheckpoint > weightStorage : false;
            const isNotlabel = !!item.position.label;
            const isMiddleIndex = index === Math.floor(via.length / 2);

            return (
              <React.Fragment key={`via-item-${item.id || index}`}>
                {hasError && isMiddleIndex && (
                  <div
                    style={{ background: "var(--brand-danger-decent)" }}
                    className="width-100pct display-flex align-items-center height-30 padding-x-10 gap-4 border rounded-small border-color-danger margin-y-4"
                  >
                    <span className="rioglyph rioglyph-delivery-warning text-size-18 text-color-black"></span>
                    <span className="text-color-black text-size-14 text-normal">
                      Capacidade de carregamento incompatível ({totalCheckpoint}/{weightStorage}),
                      <span
                        className="text-color-black cursor-pointer text-size-14 text-normal text-decoration-underline"
                        onClick={() => handleCalcMilkRun()}
                      >
                        {" "}
                        Recalcule com MilkRun
                      </span>
                    </span>
                  </div>
                )}

                <RowRoute
                  col1={
                    <div className="display-flex gap-2 align-items-end">
                      {via.length > 1 ? (
                        <div className="width-40 display-flex flex-column">
                          <button
                            className="rioglyph rioglyph-chevron-up text-color-highlight cursor-pointer bg-none border-none"
                            onClick={() => reorderArray(index, -1)}
                          ></button>
                          <button
                            className="rioglyph rioglyph-chevron-down text-color-highlight cursor-pointer bg-none border-none"
                            onClick={() => reorderArray(index, 1)}
                          ></button>
                        </div>
                      ) : null}

                      <div className="display-flex flex-column">
                        <label
                          className={
                            hasError || !isNotlabel ? "text-color-danger text-size-12" : "text-color-darker text-size-12"
                          }
                        >
                          {formatCategory(item.category)} {item.category !== "WAYPOINT" ? item.stayTime : null}
                        </label>
                        <Button
                          className="border-radius-small"
                          disabled={
                            item.category === 'PASSING_POINT' ||
                            (hasDnGroupId && isMechanicalEmergencyStop(item))
                          }
                          bsStyle={item.checkpointId ? Button.DEFAULT : Button.PRIMARY}
                          onClick={() => {
                            setSelectedStopCheckpoint(item);
                            setTypeModal("STOP");
                            setOpen(true);
                          }}
                        >
                          {item.checkpointId ? "Editar checklist" : "Vincular checklist"}
                        </Button>
                      </div>
                    </div>
                  }
                  col2={
                    <div className="width-100pct">
                      <Autosuggest
                        hasError={hasError || !isNotlabel}
                        key={`autosuggest-${item?.id || index}-${item?.position?.label || "empty"}`}
                        fetchData={fetchDataAutosuggestAddress}
                        placeholder="Informe a parada"
                        icon={"rioglyph-pushpin"}
                        defaultValue={item?.position?.label || ""}
                        defaultDescription={item?.position?.description}
                        defaultPosition={{ lat: item?.position?.lat, lng: item?.position?.lng }}
                        clearValue={!isNotlabel}
                        onClear={() => {
                          onClear(index);
                          skipVia.current = false;
                        }}
                        onSuggestionSelected={(e, i) => {
                          const copy = [...via];
                          copy[index] = {
                            ...i.suggestion,
                            stayTime: "00:00",
                            category: "REFUELING",
                            order: index,
                            position: {
                              ...(i.suggestion as any).position,
                              ...i.suggestion,
                            },
                          };
                          setVia(copy);
                        }}
                      />
                    </div>
                  }
                  col3={
                    <Button
                      disabled={!origin || !destination || !via.every((res) => res.position.lat)}
                      bsStyle={Button.PRIMARY}
                      iconOnly
                      iconName="rioglyph-plus-light"
                      onClick={() => setOpenModal(true)}
                      className="margin-left-10"
                    />
                  }
                />
              </React.Fragment>
            );
          })}

          <RowRoute
            col1={
              <div className="align-self-end display-flex flex-column">
                <label className={`text-color-dark`}>Checklist de destino</label>
                <Button
                  className="border-radius-small align-self-start"
                  disabled={!destination}
                  bsStyle={destination?.destinyChecklistId && destination?.destinyChecklistId !== '' ? Button.DEFAULT : Button.PRIMARY}
                  onClick={() => {
                    setSelectedStopCheckpoint({
                      category: 'DESTINY',
                      order: -1,
                      position: destination.position,
                      stayTime: '00:00',
                      checkpointId: destination?.destinyChecklistId,
                      id: destination.id,
                      name: destination.label,
                      note: '',
                      weight: 0,
                    });
                    setTypeModal('DESTINY');
                    setOpen(true);
                  }}
                >
                  {destination?.destinyChecklistId && destination?.destinyChecklistId !== '' ? "Editar checklist" : "Vincular checklist"}
                </Button>
              </div>
            }
            col2={
              <div className="width-100pct">
                <div
                  className={`${destinationError ? "form-group has-error" : ""}`}
                  style={{ marginBottom: 0, paddingLeft: 0 }}
                >
                  <label className={`text-color-dark ${destinationError ? "text-color-red" : ""}`}>Destino</label>
                  <Autosuggest
                    fetchData={fetchDataAutosuggestAddress}
                    placeholder="Informe o destino"
                    icon="rioglyph-finish"
                    defaultValue={destination?.label || destination?.description || ""}
                    defaultDescription={destination?.description}
                    defaultPosition={{ lat: destination?.lat, lng: destination?.lng }}
                    onClear={() => setDestination(null)}
                    onSuggestionSelected={(e, i) => {
                      const { lat, lng }: any = i?.suggestion?.position;
                      const destinationTemp = {
                        ...(i.suggestion as any).position,
                        ...i.suggestion,
                        id: i.suggestion.id,
                        destinyChecklistId: destination?.destinyChecklistId,
                      };
                      setDestination(destinationTemp);
                      dispatch(defineDestination(destinationTemp));
                      if (isEditTrip) {
                        dispatch(setTripUpdateDetailDataUpdateDestination({ ...destinationTemp, long: lng, lat: lat }));
                      }
                      setDestinationError(false);
                    }}
                  />
                </div>
              </div>
            }
          />

          <div className="margin-top-10">
            <MilkRun
              handleWeightType={handleWeightType}
              weightType={weightType}
              totalCheckpoints={totalCheckpoint}
              total={weightStorage}
              onChange={(value) => setWeightStorage(value)}
              onClick={() => handleCalcMilkRun()}
              disabled={origin && destination && !(totalCheckpoint > weightStorage)}
            />
          </div>

          <div className="separator">
            <ExpanderPanel
              title="Simulador de autonomia"
              bsStyle="blank"
              titleClassName="text-bold margin-y-10"
              bodyClassName="padding-x-20"
              open={expanded}
              onToggle={(isOpen) => dispatch(setExpanded(isOpen))}
            >
              <Dynamics.Autonomy
                active={enableAutonomy}
                toogle={(i) => dispatch(setEnableAutonomy(i))}
                fuel={FuelAutonomy}
                onChangeFuel={(item) => dispatch(setFuelAutonomy(item))}
                vision={visionAutonomy}
                onChangeVision={(item) => dispatch(setVisionAutonomy(item))}
                fuelLevel={fuelLevelAutonomy}
                onChangeFuelLevel={(level) => dispatch(setFuelLevelAutonomy(level))}
                capacity={capacityAutonomy}
                onChangeCapacity={(capacity) => dispatch(setCapacityAutonomy(capacity))}
                consume={consumeAutonomy}
                onChangeConsume={(consume) => dispatch(setConsumeAutonomy(consume))}
                onSubmit={onSuggestionVia}
              />
            </ExpanderPanel>

            <ExpanderPanel
              title="Preferências"
              bsStyle="blank"
              titleClassName="text-bold margin-y-10"
              bodyClassName="padding-0"
            >
              <DynamicContentPreferencias />
            </ExpanderPanel>

            <ExpanderPanel
              title="Características do veículo"
              bsStyle="blank"
              titleClassName="text-bold margin-y-10"
              bodyClassName="padding-0"
            >
              <Dynamics.Dimensions
                shippedHazardousGoods={shippedVHazardousGoods}
                type={type}
                handleVehicleOptionType={(str) => {
                  setType(str.toString());
                }}
                selectedTypeOption={type}
                selectedVehicleOption={selectedVehicleOption}
                vehicleType={vehicleType}
                handleChangeLenght={handleChangeLength}
                handleChangeWidth={handleChangeWidth}
                handleChangeHeight={handleChangeHeight}
                handleVehicleOptionChange={handleVehicleOptionChange}
                handleShippedHazardousGoodsType={handleShippedHazardousGoodsType}
                numberOfAxles={vehicleNumberOfAxles}
                handleVehicleNumberOfAxles={(v) => setVehicleNumberOfAxles(v)}
                trailerAxles={vehicleTrailerAxles}
                handleVehicleTrailerAxles={(v) => setVehicleTrailerAxles(v)}
                height={vehicleHeight}
                length={vehicleLenght}
                width={vehicleWidth}
                handleGrossWeight={(v) => setGrossWeight(v)}
                grossWeight={vehicleGrossWeight}
                handleMaxAxleLoad={(v) => setVehicleMaxAxleLoad(v)}
                maxAxleLoad={vehicleMaxAxleLoad}
                handleVehiclePollutantClass={(v) => setVehiclePollutantClass(v)}
                vehiclePollutantClass={vehiclePollutantClass}
              />
            </ExpanderPanel>

            <ExpanderPanel
              title="Desvio da rota"
              bsStyle="blank"
              titleClassName="text-bold margin-y-10"
              bodyClassName="padding-0"
            >
              <DynamicContentDesvio
                isChecked={hasRouteRangeLimit}
                onCheckedChange={handleHasRouteRangeLimitChange}
                onDeviationChange={handleRouteRangeLimitChange}
                rangeValue={routeRangeLimit}
              />
            </ExpanderPanel>

            <ExpanderPanel
              title="Áreas"
              bsStyle="blank"
              titleClassName="text-bold margin-y-10"
              bodyClassName="padding-0"
            >
              <Dynamics.DynamicContentArea
                onCancel={() => { }}
                pages={areaPages}
                initialData={areaData}
                onDataUpdate={(data, pages) => {
                  setAreaData(data);
                  setAreaPages(pages);
                }}
              />
            </ExpanderPanel>

            <ExpanderPanel
              title="Simulador de custos"
              bsStyle="blank"
              titleClassName="text-bold margin-y-10"
              bodyClassName="padding-0"
              open={true}
              onToggle={() => {
                setFuelOptionError(false);
              }}
            >
              <Dynamics.DynamicContentSimuilador
                isTrip={isEditTrip || isTripScreen}
                priceOperational={priceOperational}
                handlePriceOperational={(v) => setPriceOperational(v)}
                handleFuelOptionChange={handleFuelOptionChange}
                handleKmInputChange={(v) => setKmInputValue(v)}
                handlePriceInputChange={(v) => setPriceInputValue(v)}
                priceInputRef={priceInputRef}
                selectedFuelOption={selectedFuelOption}
                kmInputValue={kmInputValue}
                priceInputValue={priceInputValue}
                fuelType={fuelType.current}
                tollValue={tollValue}
                fuelValue={fuelValue}
                totalValue={totalValue}
                showResults={showResults}
                fuelOptionError={fuelOptionError}
                fuelSelectRef={fuelSelectRef}
                errorPrice={errorPrice}
                errorKm={errorKm}
              />
            </ExpanderPanel>

            {isEditTrip ? (
              <ExpanderPanel
                title="Informações da viagem"
                bsStyle="blank"
                titleClassName="text-bold margin-y-10"
                bodyClassName="padding-0"
                open
              >
                <Dynamics.DynamicTrip
                  handleEditDriverTrip={handleEditDriverTrip}
                  handleEditVehicleTrip={handleEditVehicleTrip}
                />
              </ExpanderPanel>
            ) : null}

            <div className="btn-toolbar padding-y-20 padding-x-9">
              <Button
                onClick={() => {
                  handleCheckCustom();
                  saveChanges();
                }}
                block
                bsStyle={Button.PRIMARY}
              >
                {saveLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        show={openModal}
        title={"Adicionar parada"}
        onClose={() => {
          setOpenModal(false);
        }}
        showCloseButton={false}
        headerButtons={
          <Button
            iconOnly
            iconName="rioglyph rioglyph-remove"
            className="border-none btn btn-muted"
            onClick={() => {
              setStop({ ...INITIAL_STOP });
              setOpenModal(false);
            }}
          />
        }
        bsSize="md"
        body={
          <div className="display-flex">
            <div
              style={{
                width: 56,
                height: 56,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: 24,
                marginLeft: 24,
              }}
              className="border border-color-primary border-width-3
                       text-center"
            >
              <span className="rioglyph rioglyph-pushpin text-color-primary" style={{ fontSize: 32 }} />
            </div>
            <div className="display-grid gap-10" style={{ flex: 1 }}>
              <div>
                <label className="text-color-dark">Local da parada</label>
                <Autosuggest
                  fetchData={fetchDataAutosuggestAddress}
                  placeholder="Informe a parada"
                  onClear={() => {
                    setStop((previous) => ({
                      ...previous,
                      position: null,
                    }));
                  }}
                  onSuggestionSelected={(e, i) => {
                    setInvalidPosition(false);
                    setStop((previous) => ({
                      ...previous,
                      position: {
                        ...(i.suggestion as any).position,
                        ...i.suggestion,
                      },
                    }));
                  }}
                />
                {invalidPosition ? (
                  <div
                    style={{
                      color: "red",
                      fontSize: "11px",
                      marginTop: "8px",
                    }}
                  >
                    Campo obrigatório
                  </div>
                ) : null}
              </div>
              <div>
                <label>Tipo de parada</label>
                <Select
                  id="select"
                  placeholder="Selecione"
                  options={stopTypes?.map((res) => {
                    return {
                      id: res,
                      label: formatCategory(res),
                    };
                  })}
                  onChange={(selected) => {
                    if (selected) {
                      setStop((previous) => ({
                        ...previous,
                        category: selected?.id,
                        ...(selected?.id === "PASSING_POINT" && { stayTime: "00:00" }),
                      }));
                    }
                  }}
                  value={[stop?.category]}
                  className="bg-always-black"
                  btnClassName="rounded-small"
                />
              </div>
              {stop.category !== "PASSING_POINT" ? (
                <div>
                  <label htmlFor="time">Tempo de permanência</label>
                  <input
                    id="time"
                    type="text"
                    className="form-control"
                    min="00:00"
                    // max="23:59"
                    value={stop.stayTime}
                    onChange={(event) => {
                      const formattedTime: string = formatMaskTime(event.target.value);
                      setStop((previous) => ({
                        ...previous,
                        stayTime: formattedTime,
                      }));
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        }
        footer={
          <div className="display-flex gap-10 justify-content-end">
            <Button
              bsStyle="primary"
              className="btn btn-primary btn-outline width-100"
              onClick={() => {
                setStop({ ...INITIAL_STOP });
                setOpenModal(false);
                setInvalidPosition(false);
              }}
            >
              Cancelar
            </Button>
            <Button
              disabled={stop.category !== "PASSING_POINT" ? stop.stayTime === "00:00" : !stop.position}
              bsStyle="primary"
              className="width-100"
              onClick={() => {
                if (!stop.position) {
                  setInvalidPosition(true);
                  return;
                }
                setStop({ ...INITIAL_STOP });
                setInvalidPosition(false);
                setVia((previous) => [
                  ...previous,
                  { ...stop, order: previous.length + 1, checkpointId: stop.checkpointId },
                ]);
                setOpenModal(false);
              }}
            >
              Salvar
            </Button>
          </div>
        }
      />
      <SelectCheckpoint
        open={open}
        onClose={setOpen}
        selected={selectedStopCheckpoint}
        handleSelectCheckpoint={handleUpdateForType}
        handleRemoveCheckpoint={handleUpdateForType}
      />
    </>
  );
};
export default RegisterRoute;
